import { useState } from "react";
import { format, startOfToday } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0"));

// Value format: "YYYY-MM-DDTHH:mm" (same as datetime-local, ISO-parseable)
export function DateTimePicker({ value, onChange, testId, placeholder = "Pick date & time" }) {
  const [open, setOpen] = useState(false);
  const date = value ? new Date(value) : undefined;
  const hh = value ? value.slice(11, 13) : "";
  const mm = value ? value.slice(14, 16) : "";

  const commit = (d, h, m) => {
    if (!d) return;
    onChange(`${format(d, "yyyy-MM-dd")}T${h || "09"}:${m || "00"}`);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className="w-full justify-start font-normal" data-testid={testId}>
          <CalendarIcon className="w-4 h-4 mr-2 shrink-0" />
          {date ? format(date, "dd MMM yyyy, HH:mm") : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={date} disabled={{ before: startOfToday() }} onSelect={(d) => d && commit(d, hh, mm)} />
        <div className="flex items-center gap-2 border-t border-border/60 p-3">
          <span className="text-xs text-muted-foreground">Time</span>
          <Select value={hh} onValueChange={(h) => commit(date || new Date(), h, mm)}>
            <SelectTrigger className="h-8 w-[70px]" data-testid={testId ? `${testId}-hour` : undefined}>
              <SelectValue placeholder="HH" />
            </SelectTrigger>
            <SelectContent>
              {HOURS.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground">:</span>
          <Select value={mm} onValueChange={(m) => commit(date || new Date(), hh || "09", m)}>
            <SelectTrigger className="h-8 w-[70px]" data-testid={testId ? `${testId}-minute` : undefined}>
              <SelectValue placeholder="MM" />
            </SelectTrigger>
            <SelectContent>
              {MINUTES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" className="h-8 rounded-full bg-[#0B1B3D] ml-auto" onClick={() => setOpen(false)} data-testid={testId ? `${testId}-done` : undefined}>
            Done
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
